/**
 * @flow
 */
import { Box, Typography } from '@mui/material';
import OccurrenceDrawerContent from 'components/@whistleblower-admin/drawers/OccurrenceDrawer/OccurrenceDrawerContent';
import columns from 'components/@whistleblower-admin/pages/OccurrenceList/columns';
import syncOccurrenceDrawer from 'components/@whistleblower-admin/pages/OccurrenceList/occurrenceDrawerSync';
import useDecrypt from 'components/@whistleblower-admin/pages/OccurrenceList/useDecrypt';
import { useWhistleblower } from 'components/@whistleblower-admin/WhistleblowerContext';
import { useDrawer } from 'components/common/Drawer/DrawerContext';
import DrawerProvider from 'components/common/Drawer/DrawerProvider';
import OmmnioDataGrid from 'components/common/OmmnioDataGrid';
import moment from 'moment';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { I18n } from 'utils/i18n';
import { useNavigate, useOutletContext } from 'react-router';
import { Outlet, useParams } from 'react-router';
import { useGetOccurrencesQuery } from 'store/app/entities/WbSlice';

export const OpenOccurrence = () => {
  const { rows, drawerSyncRef } = useOutletContext();
  const { id } = useParams();
  const { show, hide, visible } = useDrawer('OccurrenceDrawer');
  useEffect(() => {
    const next = syncOccurrenceDrawer({
      id,
      rows,
      visible,
      closing: drawerSyncRef.current.closing,
      lastId: drawerSyncRef.current.lastId,
    });
    drawerSyncRef.current = { closing: next.closing, lastId: next.lastId };
    if (next.action === 'show') show(next.occurrence);
    else if (next.action === 'hide') hide();
  }, [drawerSyncRef, hide, id, rows, show, visible]);
  // Clear only on unmount. This route goes away when the list URL drops the id,
  // so the sync effect never sees the cleared id. Resetting inside that effect
  // would run on the close render, while the id is still stale, and reopen the drawer.
  useEffect(
    () => () => {
      drawerSyncRef.current = { closing: false, lastId: undefined };
    },
    [drawerSyncRef],
  );
  return null;
};

const OccurrenceList = () => {
  const [pageSize, setPageSize] = useState(0);
  const { publicKey, privateKey } = useWhistleblower();
  const locale = useSelector(state => state.i18n.locale);
  const { data, isLoading, isUninitialized } = useGetOccurrencesQuery(
    { body: { publicKey } },
    { skip: !publicKey || !pageSize },
  );
  const { rows, decrypting } = useDecrypt(data?.docs, privateKey) || [];
  const navigate = useNavigate();
  const drawerSyncRef = useRef({ closing: false, lastId: undefined });
  const handleDrawerClose = useCallback(() => {
    drawerSyncRef.current.closing = true;
    navigate('/wb-admin/occurrences', { replace: true });
  }, [navigate]);

  moment.locale(locale);

  const handleRowClick = ({ id }) => {
    navigate(`/wb-admin/occurrences/${id}`);
  };

  return (
    <Box
      sx={{
        p: 3,
        flexGrow: 1,
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
      }}
    >
      <Typography variant="h2" gutterBottom>
        {I18n.t('WhistleblowerAdmin.OccurrenceList.title')}
      </Typography>
      <DrawerProvider
        name="OccurrenceDrawer"
        drawerContent={<OccurrenceDrawerContent />}
        onClose={handleDrawerClose}
      >
        <OmmnioDataGrid
          pageSize={pageSize}
          onPageSizeChange={setPageSize}
          sx={{ cursor: 'pointer' }}
          onRowClick={handleRowClick}
          loading={isUninitialized || isLoading || decrypting}
          autoPageSize
          columns={columns(locale)}
          rows={rows || []}
        />
        <Outlet context={{ rows, drawerSyncRef }} />
      </DrawerProvider>
    </Box>
  );
};

export default OccurrenceList;
